/** @jsxImportSource @emotion/react */
import React, { useEffect, useState, useMemo, useRef, useImperativeHandle } from 'react';
import useEvent from 'react-use-event-hook';
import { useTranslation } from 'react-i18next';
import _, { toNumber } from 'lodash';
import { Button } from '@mui/material';
import * as input from '@grapecity/wijmo.input';
import { DataType } from '@grapecity/wijmo';
import { DataMap, CellType } from '@grapecity/wijmo.grid';
import {
  SubTitleLayout,
  SubTitleGroup,
  GlobalBtnGroup,
  ControlBtnGroup,
} from 'components/layouts/ContentLayout';
import CustomGrid from 'components/grids/CustomGrid';
import { IconButton } from 'components/buttons/IconSVG';
import { UtMatrixDetail, UtMatrixRegistSearchCondition } from 'models/ut/UtMatrix';
import { commonYNcodes } from 'models/common/Common';
import { downloadExcelTemplates } from 'apis/common/Excel';
import {
  changeStatUtMatix,
  findProcess,
  findProcessBar,
  findUtMatrix,
  revCpltUtMatixMail,
  saveUtMatrix,
} from 'apis/ut/UtMatrixRegist';
import { useLoading } from 'components/process/Loading';
import { useCommonModal } from 'hooks/useCommonModal';
import { useMessageBar } from 'hooks/useMessageBar';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import { Code } from 'models/common/CommonCode';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { calculateDmdElpwCapa, convertNumber } from 'utils/UtMatrixUtil';
import { CommonUtil } from 'utils/CommonUtil';
import UtMatrixHookupTagPopup from '../popup/UtMatrixHookupTagPopup';
import UtMatrixRegistHistoryPoupup from '../popup/UtMatrixRegistHistoryPoupup';
import { hasRole } from 'utils/SessionUtil';
import {
  DefaultNoneNumberCellTemplate,
  DefaultZeroNumberCellTemplate,
} from 'components/grids/NumberCellRenderer';
import { ro } from 'date-fns/locale';
import GridSettingButtons from '../../../components/grids/GridSettingButtons';
import * as wjGridXlsx from '@grapecity/wijmo.grid.xlsx';
import { getExcelFileName } from 'utils/ExcelUtil';
import { CrudCode } from 'models/common/Edit';

/**
 * @desc UT Matrix 상세보기 탭 - DetailSummaryContent
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2025.09.16  sungim.kim2    검토완료 버튼 추가
 * 2025.09.29  jshwang12      [고도화] 표준사용량 로직 변경 (표준설비코드 기준 라이브러리의 평균값 노출(저장X), 라이브러리의 값이 없는 경우 대상에서 제외 (0 포함))
 *                            [고도화] 열 순서, Size 열 명칭 변경 (O.D -> OUT, I.D -> IN)
 *                            [고도화] Exhaust air : Collector type, Temperature 항목 추가
 *                            [고도화] Supply air : Temperature 추가
 *                            [고도화] CDA, N2 단위 변경 (Nl/min)
 *                            [고도화] 전력/CDA 실사용량 추가 (EUM Tag 최대 수치값 노출(저장X))
 * 2025.10.01  jshwang12      자연수(ex. 수량)를 제외한 숫자는 소수점 1자리까지 표현(2자리에서 반올림 처리) (ex. 230.456 입력 시 실제 데이터는 230.5 저장)
 * 2025.10.01  jshwang12      숫자 표현 방식에서 Power Factor만 예외로 소수점 2자리까지 표현(3자리에서 반올림 처리)
 * 2025.10.01  jshwang12      Return Air > Q'ty, Supply air > Consumption[CMH] 항목 읽기전용으로 변경 (cf. 검토화면 편집 가능 항목: UT Matrix 탭 - 전기 수용율, 기계요약 탭 - 수용율)
 * 2025.11.03  jihwan89       핀 고정 해제
 * 2026.01.26  jihwan89       CSR E20260106-000678 검토화면 천단위 숫자 인식되게 변경(컴마삭제)
 * 2026.02.09  jihwan89       수용율 변경 후 재작성요청, 보류, 검증, 검토진행 시 알림 메세지 오픈 및 임시저장
 * 2026.03.24  jihwan89       E20260318-001141 핀 고정 원복 -> Machine에서 고정
 * 2026.08.05   jihwan89      CSR E20260727-000228 Vacuum Pump 추가
 */
const DetailSummaryContent = (props: any, ref) => {
  const {
    onSubmit,
    condition = {} as UtMatrixRegistSearchCondition,
    processData = [] as UtMatrixDetail[],
  } = props;
  const gridRef = useRef<any>();
  const gridRef2 = useRef<any>();
  const [isChangeColumn, setIsChangeColumn] = useState<boolean>(false);
  const [flexRef, setFlexRef] = useState<any>();
  const { t } = useTranslation();
  const { openLoading } = useLoading();
  const { openCommonModal } = useCommonModal();
  const { openMessageBar } = useMessageBar();
  const [rowData, setRowData] = useState<UtMatrixDetail[]>([]);
  const [isOpenHookupTagPopup, setOpenHookupTagPopup] = useState<boolean>(false);
  const [hookupTagCondition, setHookupTagCondition] = useState<any>();
  const [isUtMatrixRegistHistoryModalOpen, setUtMatrixRegistHistoryModalOpen] =
    useState<boolean>(false);
  const [utMatrixRegistHistoryCondition, setUtMatrixRegistHistoryCondition] = useState<any>();
  const [code, setCode] = useState<any>();
  const [hasAuth, setHasAuth] = useState<boolean>(false);
  const defaultGridHeight = 205 + 30; //---- // ㅇㅇㅇㅇㅇ

  const isAprText = useMemo(() => {
    const p = processData.filter((o) => o.utmWrtProcProgStatCd === 'UTP05');

    return condition.utmWkProgStatCd === 'UTM04' && p.length > 0
      ? String(t('com.button.결재정보', '결재정보'))
      : String(t('com.button.결재요청', '결재요청'));
  }, [processData, condition.utmWkProgStatCd]);

  // 2025.09.16 sungim.kim2 검토완료 <재작성요청><보류><결재요청>
  const isUtReviewComplete = useMemo(() => {
    return condition.utmWkProgStatCd === 'UTM07' && condition.openMode !== 'appr' && hasAuth;
  }, [condition.utmWkProgStatCd, hasAuth]);

  //검토중 <재작성요청><보류><검증><임시저장><결재요청>
  const isUtReview = useMemo(() => {
    return condition.utmWkProgStatCd === 'UTM04' && condition.openMode !== 'appr' && hasAuth;
  }, [condition.utmWkProgStatCd, hasAuth]);

  // 작성완료 : <검토진행> <재작성요청> <보류> (P 결재중인문서체크)
  const isUtComplete = useMemo(() => {
    const p = processData.filter((o) => o.utmWrtProcProgStatCd === 'UTP05');
    return condition.utmWkProgStatCd === 'UTM03' && p.length === 0 && hasAuth;
  }, [processData, condition.utmWkProgStatCd, hasAuth]);

  // 작성중 : <검증> <임시저장>
  const isUtWriter = useMemo(() => {
    return condition.utmWkProgStatCd === 'UTM02' && hasAuth; //작성중
  }, [processData, condition.utmWkProgStatCd, hasAuth]);

  useImperativeHandle(ref, () => ({
    searchRegistMatrix: (params) => {
      searchRegistMatrix(params);
    },
    saveRegistMatrix: (showMsg = true) => {
      return new Promise((resolve) => {
        handleSave(showMsg).finally(() => {
          resolve(true);
        });
      });
    },
  }));

  useEffect(() => {
    if (hasRole('ADM') || hasRole('UT_MANAGER')) {
      setHasAuth(true);
    }
    getCommonCodesForGrid();
  }, []);

  const getCommonCodesForGrid = async () => {
    const bldgFloCd: Code[] = await getCommonCodeNames('BLDG_FLO_CD');
    const utVltgList: Code[] = await getCommonCodeNames('UT_VLTG_LIST');
    const elpwPhasCd: Code[] = await getCommonCodeNames('ELPW_PHAS_CD');
    const ctwTpCd: Code[] = await getCommonCodeNames('CTW_TP_CD');
    const utFrqList: Code[] = await getCommonCodeNames('UT_FRQ_LIST');
    const elpwEqpCnctTpCd: Code[] = await getCommonCodeNames('ELPW_EQP_CNCT_TP_CD');
    const devcCnctTpCd: Code[] = await getCommonCodeNames('DEVC_CNCT_TP_CD');
    const exhaCllrTpCd: Code[] = await getCommonCodeNames('EXHA_CLLR_TP_CD');
    const utBrkeCapaList: Code[] = await getCommonCodeNames('UT_BRKE_CAPA_LIST');
    const insTpCd: Code[] = await getCommonCodeNames('INS_TP_CD');
    // const suarCllrTpCd: Code[] = await getCommonCodeNames('SUAR_CLLR_TP_CD');

    setCode({
      bldgFloCd: bldgFloCd,
      utVltgList: utVltgList,
      elpwPhasCd: elpwPhasCd,
      ctwTpCd: ctwTpCd,
      utFrqList: utFrqList,
      elpwEqpCnctTpCd: elpwEqpCnctTpCd,
      devcCnctTpCd: devcCnctTpCd,
      exhaCllrTpCd: exhaCllrTpCd,
      utBrkeCapaList: utBrkeCapaList,
      insTpCd: insTpCd,
      // suarCllrTpCd: suarCllrTpCd,
    });
  };

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'utmSeq',
        header: String(t('com.label.NO', 'NO')),
        minWidth: 40,
        maxWidth: 50,
        isReadOnly: true,
        align: 'center',
      },
      {
        header: String(t('com.label.상태', '상태')),
        binding: 'crudKey',
        minWidth: 40,
        width: 40,
        isReadOnly: true,
        align: 'center',
        cellTemplate: GridStatusCellTemplate,
      },
      {
        binding: 'prdnProcCdNm',
        header: String(t('ut.label.공정', '공정')),
        minWidth: 80,
        width: 100,
        isReadOnly: true,
        align: 'center',
      },
      {
        header: String(t('ut.label.AREA', 'AREA')),
        columns: [
          {
            binding: 'bizAreaNm',
            header: ' ',
            minWidth: 65,
            width: 68,
            align: 'center',
            isReadOnly: true,
          },
        ],
      },
      {
        binding: 'eqclNm',
        header: String(t('ut.label.설비군', '설비군')),
        minWidth: 80,
        width: 100,
        isReadOnly: true,
      },
      {
        binding: 'dtalProcCdNm',
        header: String(t('ut.label.세부공정', '세부공정')),
        minWidth: 80,
        width: 110,
        isReadOnly: true,
      },
      {
        header: String(t('ut.label.Facility Location', 'Facility Location')),
        //collapseTo: 'bldgFloCd',
        isCollapsed: false,
        columns: [
          {
            binding: 'bldgFloCd',
            header: String(t('ut.label.Floor', 'Floor')),
            minWidth: 80,
            width: 120,
            isReadOnly: true,
            align: 'center',
            cssClass: 'WijmoSelect',
            dataMap: new DataMap(code?.bldgFloCd || [], 'cmnCd', 'cmnCdNm'),
          },
          {
            binding: 'istlLocNm',
            header: String(t('ut.label.Room', 'Room')),
            minWidth: 80,
            width: 100,
            isReadOnly: true,
          },
        ],
      },
      {
        header: String(t('ut.label.Line', 'Line')),
        columns: [
          {
            binding: 'prdnLnNm',
            header: ' ',
            minWidth: 60,
            width: 68,
            isReadOnly: true,
            align: 'center',
          },
        ],
      },
      {
        header: String(t('ut.label.Sub Line', 'Sub Line')),
        columns: [
          {
            binding: 'prdnPrlLnNm',
            header: '　',
            minWidth: 60,
            width: 68,
            align: 'center',
            isReadOnly: true,
          },
        ],
      },
      {
        binding: 'eqpMainNm',
        header: String(t('ut.label.Main', 'Main')),
        minWidth: 80,
        width: 100,
        isReadOnly: true,
      },
      {
        binding: 'eqpMchNm',
        header: String(t('ut.label.Machine', 'Machine')),
        minWidth: 80,
        width: 120,
        isReadOnly: true,
      },
      {
        header: String(t('ut.label.Unit', 'Unit')),
        columns: [
          {
            binding: 'eqpUntNm',
            header: ' ',
            minWidth: 60,
            width: 68,
            isReadOnly: true,
          },
        ],
      },
      {
        // 시스템 자동 (표준설비코드 Library 선택에 따라 자동으로 설정)
        // 엑셀에서 업로드한 경우는 작성구분을 사용자가 입력한 값을 지정하고 작성구분에 따라 제출 시 표준설비코드, LibraryID의 선택여부를 interlock 한다.
        binding: 'insTpCd',
        header: String(t('ut.label.작성구분', '작성구분')),
        minWidth: 80,
        width: 100,
        align: 'center',
        isReadOnly: true,
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(code?.insTpCd || [], 'cmnCd', 'cmnCdNm'),
      },
      {
        binding: 'stndEqpId',
        header: String(t('ut.label.표준설비코드', '표준설비코드')),
        minWidth: 80,
        width: 140,
        isReadOnly: true,
        align: 'center',
        cssClass: 'WijmoFind',
        cellTemplate: (params) => `
            <span>${params.value || ''}</span> 
            ${params.value ? '<Button id="stndEqpId" />' : ''} 
        `,
      },
      {
        header: String(t('ut.label.Library', 'Library')),
        columns: [
          {
            binding: 'utmLibId',
            header: String(t('ut.label.ID', 'ID')),
            minWidth: 80,
            width: 100,
            isReadOnly: true,
          },
          {
            binding: 'utmLibVerNo',
            header: String(t('ut.label.Version', 'Version')),
            minWidth: 80,
            width: 100,
            isReadOnly: true,
          },
        ],
      },
      {
        header: String(t('ut.label.Hookup Tagging', 'Hookup Tagging')),
        //   collapseTo: 'machEqpNm',
        columns: [
          {
            // 검토/결과 보기 화면에서는 Hookup ID 생성 자동 표시
            binding: 'machEqpNm',
            header: String(t('ut.label.Mech', 'Mech')),
            minWidth: 80,
            width: 100,
            isReadOnly: true,
            cssClass: 'WijmoFind',
            cellTemplate: (params) => `
                <span>${params.value || ''}</span>   
                ${params.value && params.item.tagIssuYn === 'Y' ? '<Button />' : ''} 
            `,
          },
          {
            // 검토/결과 보기 화면에서는 Hookup ID 생성 자동 표시
            binding: 'elpwEqpNm',
            header: String(t('ut.label.Elec', 'Elec')),
            minWidth: 80,
            width: 100,
            isReadOnly: true,
            cssClass: 'WijmoFind',
            cellTemplate: (params) => `
                <span>${params.value || ''}</span> 
                ${params.value && params.item.tagIssuYn === 'Y' ? '<Button />' : ''}
            `,
          },
        ],
      },
      {
        header: String(t('ut.label.설비 Qty', '설비 Qty')),
        columns: [
          {
            binding: 'machEqpQty',
            header: String(t('ut.label.Mech', 'Mech')),
            minWidth: 80,
            width: 100,
            isReadOnly: true,
            align: 'right',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
          {
            binding: 'elpwEqpQty',
            header: String(t('ut.label.Elec', 'Elec')),
            minWidth: 80,
            width: 100,
            isReadOnly: true,
            align: 'right',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        header: String(t('ut.label.Electricity', 'Electricity')),
        //      collapseTo: 'vltgNvl',
        columns: [
          /*
          // [24.11.19] 미사용 삭제
          {
            binding: 'elpwEqpGrNm',
            header: String(t('ut.label.Group 명', 'Group 명')),
            width: 100,
            isReadOnly: true,
          },
          */
          {
            header: String(t('ut.label.Voltage', 'Voltage')),
            columns: [
              {
                binding: 'vltgNvl', // 전기필수 (ELPW_EQP_QTY 값이 1 이상일때 필수)
                header: '[V]',
                minWidth: 60,
                width: 75,
                isReadOnly: true,
                align: 'center',
                cssClass: 'WijmoSelect',
                dataMap: new DataMap(code?.utVltgList || [], 'cmnCd', 'cmnCdNm'),
              },
            ],
          },
          {
            header: String(t('ut.label.Phase', 'Phase')),
            columns: [
              {
                binding: 'elpwPhasCd', // 전기필수
                header: '[Φ]',
                minWidth: 60,
                width: 75,
                isReadOnly: true,
                align: 'center',
                cssClass: 'WijmoSelect',
                dataMap: new DataMap(code?.elpwPhasCd || [], 'cmnCd', 'cmnCdNm'),
              },
            ],
          },
          {
            header: String(t('ut.label.Wires', 'Wires')),
            columns: [
              {
                binding: 'ctwTpCd',
                header: '[EA]',
                minWidth: 60,
                width: 80,
                isReadOnly: true,
                align: 'center',
                cssClass: 'WijmoSelect',
                dataMap: new DataMap(code?.ctwTpCd || [], 'cmnCd', 'cmnCdNm'),
              },
            ],
          },
          {
            // [25.03.10] 0.95로 Default 입력
            // [25.09.25] 0.95 기본값 노출 후 수정가능 (고도화)
            header: String(t('ut.label.Power Factor', 'Power Factor')),
            columns: [
              {
                binding: 'pwftNvl', // 전기필수
                header: '-',
                minWidth: 60,
                width: 86,
                align: 'right',
                dataType: DataType.Number,
                isReadOnly: true,
                // [25.10.01] Power Factor는 소수점 2자리까지 표현 (3자리에서 반올림 처리)
                cellTemplate: (params) =>
                  Number(params.item.elpwEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params, 2)
                    : DefaultZeroNumberCellTemplate(params, 2),
              },
            ],
          },
          /*
          // [25.09.23] 화면에서 삭제 (고도화)
          {
            // UT Matrix (UT Matrix No)별로 동일해야 함
            binding: 'frqNvl', // 전기필수
            header: String(t('ut.label.Frequency[Hz]', 'Frequency[Hz]')),
            width: 100,
            isReadOnly: true,
            visible: false,
            cssClass: 'WijmoSelect',
            dataMap: new DataMap(code?.utFrqList || [], 'cmnCd', 'cmnCdNm'),
          },
          */
          {
            header: String(t('ut.label.Current', 'Current')),
            columns: [
              {
                binding: 'od2EcNvl',
                header: String(t('ut.label.입력값 [A]', '입력값 [A]')),
                minWidth: 80,
                width: 97,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.elpwEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
              {
                binding: 'od1EcNvl', // 수식 시스템 계산
                header: String(t('ut.label.계산값 [A]', '계산값 [A]')),
                minWidth: 80,
                width: 97,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.elpwEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          /*
          // [25.09.23] 화면에서 삭제 (고도화)
          {
            header: String(t('ut.label.동시가동율', '동시가동율')),
            columns: [
              {
                binding: 'smtmOprt',
                // header: String(t('ut.label.동시가동율[%]', '동시가동율[%]')),
                header: '[%]',
                width: 75,
                isReadOnly: true,
                dataType: DataType.Number,
              },
            ],
          },
          */
          {
            header: String(t('ut.label.Breaker', 'Breaker')),
            columns: [
              {
                binding: 'brkeCapa',
                header: String(t('ut.label.입력값 [A]', '입력값 [A]')),
                minWidth: 80,
                width: 97,
                align: 'right',
                isReadOnly: true,
                cellTemplate: (params) =>
                  Number(params.item.elpwEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
              {
                binding: 'optValCtn1',
                header: String(t('ut.label.계산값 [A]', '계산값 [A]')),
                minWidth: 80,
                width: 97,
                align: 'right',
                isReadOnly: true,
                cellTemplate: (params) =>
                  Number(params.item.elpwEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          {
            binding: 'plcClctYn', // 전기필수
            header: String(t('ut.label.PLC접지유무-', 'PLC접지유무-')),
            minWidth: 80,
            width: 100,
            isReadOnly: true,
            visible: false,
            cssClass: 'WijmoSelect',
            dataMap: new DataMap(commonYNcodes, 'cmnCd', 'cmnCdNm'),
          },
          {
            header: String(t('ut.label.Connection', 'Connection')),
            columns: [
              {
                binding: 'elpwEqpCnctTpCd', // 전기필수
                header: '[Type]',
                minWidth: 60,
                width: 80,
                isReadOnly: true,
                align: 'center',
                cssClass: 'WijmoSelect',
                dataMap: new DataMap(code?.elpwEqpCnctTpCd || [], 'cmnCd', 'cmnCdNm'),
              },
            ],
          },
          {
            header: String(t('ut.label.Capacity', 'Capacity')),
            columns: [
              {
                binding: 'elpwCapa', // 전기필수
                header: '[kW]',
                minWidth: 80,
                width: 83,
                align: 'right',
                isReadOnly: true,
                cellTemplate: (params) =>
                  Number(params.item.elpwEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
              {
                binding: 'elpwSumCapa', // 수식계산 (= 설비 Qty Elec * Capacity[KW])
                header: 'Sum',
                minWidth: 80,
                width: 83,
                align: 'right',
                isReadOnly: true,
                cellTemplate: (params) =>
                  Number(params.item.elpwEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          {
            /**
             * [25.09.23] 표준사용량 로직 변경
             * - 표준설비코드 기준 라이브러리의 평균값 노출
             * - 라이브러리의 값이 없는 경우 대상에서 제외 (0 포함)
             */
            header: String(t('ut.label.표준사용량', '표준사용량')),
            columns: [
              {
                binding: 'elpwStndUseQty',
                header: '[kW]',
                minWidth: 70,
                width: 82,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.elpwEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          {
            // [25.09.23] EUM의 Peak 값 표시
            header: String(t('ut.label.실사용량', '실사용량')),
            columns: [
              {
                binding: 'elpwEumMaxNvl',
                header: '[kW]',
                minWidth: 70,
                width: 82,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.elpwEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          {
            header: String(t('ut.label.Heat Capacity', 'Heat Capacity')),
            columns: [
              {
                binding: 'elpwHeatCapa', // 전기필수
                header: '[kW]',
                minWidth: 70,
                width: 88,
                align: 'right',
                isReadOnly: true,
                cellTemplate: (params) =>
                  Number(params.item.elpwEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          {
            header: String(t('ut.label.Demand Factor', 'Demand Factor')),
            columns: [
              {
                binding: 'dmdElpwTarfNvl', // 검토화면에서 UT Manager 권한인 경우 입력
                header: '[%]',
                minWidth: 70,
                width: 96,
                isRequired: false,
                dataType: DataType.Number,
                format: 'n1',
                editor: new input.InputNumber(document.createElement('div'), {
                  format: 'n1',
                }),
                cellTemplate: (params) =>
                  Number(params.item.elpwEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          {
            header: String(t('ut.label.Capacity_Sum', 'Capacity_Sum')),
            columns: [
              {
                binding: 'dmdElpwCapa', // 검토화면에서 자동계산 (= Capacity Sum * Demand Factor[%])
                header: String(t('ut.label.(수용율 적용) [kW]', '(수용율 적용) [kW]')),
                minWidth: 100,
                width: 160,
                align: 'right',
                isReadOnly: true,
                cellTemplate: (params) =>
                  Number(params.item.elpwEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          /*
          // 개선요청사항(김중열) 57번 - 13. Static Condenser Capa. 해당 열 삭제
          {
            binding: 'cdsCapa', // 검토화면에서 UT Manager 권한인 경우 입력
            header: String(
              t(
                'ut.label.Static Condenser Capa. (p.f. = 0.92)[KVAR]',
                'Static Condenser Capa. (p.f. = 0.92)[KVAR]'
              )
            ),
            width: 100,
            isRequired: false,
            dataType: DataType.Number,
          },
          */
        ],
      },
      {
        header: String(t('ut.label.CDA', 'CDA')),
        //      collapseTo: 'cdaOtsdSizeValCtn',
        columns: [
          {
            header: String(t('ut.label.Size', 'Size')),
            columns: [
              {
                binding: 'cdaOtsdSizeValCtn',
                header: String(t('ut.label.입력값', '입력값')),
                minWidth: 80,
                width: 85,
                isReadOnly: true,
              },
              {
                binding: 'none',
                header: String(t('ut.label.추천값', '추천값')),
                isReadOnly: true,
                minWidth: 70,
                width: 75,
              },
            ],
          },
          {
            header: String(t('ut.label.Connection', 'Connection')),
            columns: [
              {
                binding: 'cdaDevcCnctTpCd',
                header: '[Type]',
                minWidth: 70,
                width: 90,
                cssClass: 'WijmoSelect',
                dataMap: new DataMap(code?.devcCnctTpCd || [], 'cmnCd', 'cmnCdNm'),
                isReadOnly: true,
              },
            ],
          },
          {
            header: String(t('ut.label.Pressure', 'Pressure')),
            columns: [
              {
                binding: 'cdaPresValCtn', // [24.11.11] 사용자선택 -> 사용자입력 변경 (데이터 검증 제외)
                header: '[Bar]',
                minWidth: 70,
                width: 80,
                isReadOnly: true,
              },
            ],
          },
          {
            header: String(t("ut.label.Q'ty", "Q'ty")),
            columns: [
              {
                binding: 'cdaPntCnt',
                header: '[Point]',
                minWidth: 70,
                width: 92,
                align: 'right',
                isReadOnly: true,
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          {
            header: String(t('ut.label.Consumption', 'Consumption')),
            columns: [
              {
                binding: 'cdaCspCapa', // 기계 조건부 필수 대상값
                header: '[Nl/min]',
                minWidth: 80,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
              {
                binding: 'cdaCspSumCapa', // 수식계산 (= Qty * CDA Consumption[ℓ/min])
                header: 'Sum',
                minWidth: 80,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          {
            /**
             * [25.09.23] 표준사용량 로직 변경
             * - 표준설비코드 기준 라이브러리의 평균값 노출
             * - 라이브러리의 값이 없는 경우 대상에서 제외 (0 포함)
             */
            header: String(t('ut.label.표준사용량', '표준사용량')),
            columns: [
              {
                binding: 'cdaStndUseQty',
                header: '[Nl/min]',
                minWidth: 80,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          {
            // [25.09.23] EUM의 Peak 값 표시
            header: String(t('ut.label.실사용량', '실사용량')),
            columns: [
              {
                binding: 'cdaEumMaxNvl',
                header: '[Nl/lpm]',
                minWidth: 70,
                width: 82,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
        ],
      },
      {
        header: String(t('ut.label.N2', 'N2')),
        //    collapseTo: 'nitrOtsdDmtrValCtn',
        columns: [
          {
            header: 'Size',
            columns: [
              {
                binding: 'nitrOtsdDmtrValCtn',
                header: String(t('ut.label.입력값', '입력값')),
                minWidth: 70,
                width: 85,
                isReadOnly: true,
              },
              {
                binding: 'none',
                header: String(t('ut.label.추천값', '추천값')),
                isReadOnly: true,
                minWidth: 60,
                width: 75,
              },
            ],
          },
          {
            header: String(t('ut.label.Connection', 'Connection')),
            columns: [
              {
                binding: 'nitrDevcCnctTpCd',
                header: '[Type]',
                minWidth: 70,
                width: 90,
                isReadOnly: true,
                cssClass: 'WijmoSelect',
                dataMap: new DataMap(code?.devcCnctTpCd || [], 'cmnCd', 'cmnCdNm'),
              },
            ],
          },
          {
            header: String(t('ut.label.Pressure', 'Pressure')),
            columns: [
              {
                binding: 'nitrPresValCtn', // [24.11.11] 사용자선택 -> 사용자입력 변경 (데이터 검증 제외)
                header: '[Bar]',
                minWidth: 70,
                width: 80,
                isReadOnly: true,
              },
            ],
          },
          {
            header: String(t("ut.label.Q'ty", "Q'ty")),
            columns: [
              {
                binding: 'nitrPntCnt',
                header: '[Point]',
                minWidth: 70,
                width: 92,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          {
            header: String(t('ut.label.AVG Consumption', 'AVG Consumption')),
            columns: [
              {
                binding: 'nitrCspCapa',
                header: '[Nl/min]',
                minWidth: 70,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
              {
                binding: 'nitrCspSumCapa',
                header: 'Sum',
                minWidth: 70,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          {
            header: String(t('ut.label.Peak Consumption', 'Peak Consumption')),
            columns: [
              {
                binding: 'nitrPeakCspCapa', // 기계 조건부 필수 대상값
                header: '[Nl/min]',
                minWidth: 70,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
              {
                binding: 'nitrPeakCspSumCapa', // 수식 (= Qty * N2 Consumption[ℓ/min])
                header: 'Sum',
                minWidth: 70,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          {
            /**
             * [25.09.23] 표준사용량 로직 변경
             * - 표준설비코드 기준 라이브러리의 평균값 노출
             * - 라이브러리의 값이 없는 경우 대상에서 제외 (0 포함)
             */
            header: String(t('ut.label.표준사용량', '표준사용량')),
            columns: [
              {
                binding: 'nitrStndUseQty',
                header: '[Nl/min]',
                minWidth: 70,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
        ],
      },
      {
        header: String(t('ut.label.IW', 'IW')),
        //    collapseTo: 'iwOtsdDmtrValCtn',
        columns: [
          {
            header: String(t('ut.label.Size', 'Size')),
            columns: [
              {
                binding: 'iwOtsdDmtrValCtn',
                header: String(t('ut.label.입력값', '입력값')),
                minWidth: 70,
                width: 85,
                isReadOnly: true,
              },
              {
                binding: 'none',
                header: String(t('ut.label.추천값', '추천값')),
                isReadOnly: true,
                minWidth: 60,
                width: 75,
              },
            ],
          },
          {
            header: String(t('ut.label.Connection', 'Connection')),
            columns: [
              {
                binding: 'iwDevcCnctTpCd',
                header: '[Type]',
                minWidth: 70,
                width: 90,
                isReadOnly: true,
                cssClass: 'WijmoSelect',
                dataMap: new DataMap(code?.devcCnctTpCd || [], 'cmnCd', 'cmnCdNm'),
              },
            ],
          },
          {
            header: String(t('ut.label.Tempurature', 'Tempurature')),
            columns: [
              {
                binding: 'iwTmprValCtn', // [24.11.11] 숫자 -> 문자 변경 (데이터 검증 제외)
                header: '[℃]',
                minWidth: 70,
                width: 85,
                isReadOnly: true,
              },
            ],
          },
          {
            header: String(t('ut.label.Pressure', 'Pressure')),
            columns: [
              {
                binding: 'iwPresValCtn', // [24.11.11] 숫자 -> 문자 변경 (데이터 검증 제외)
                header: '[Bar]',
                minWidth: 70,
                width: 80,
                isReadOnly: true,
              },
            ],
          },
          {
            header: String(t("ut.label.Q'ty", "Q'ty")),
            columns: [
              {
                binding: 'iwPntCnt',
                header: '[Point]',
                minWidth: 70,
                width: 92,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          {
            header: String(t('ut.label.Drain', 'Drain')),
            columns: [
              {
                binding: 'iwDrnCapa',
                header: '[ℓ/min]',
                minWidth: 70,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
              {
                binding: 'iwDrnSumCapa',
                header: 'Sum',
                minWidth: 70,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          {
            header: String(t('ut.label.Consumption', 'Consumption')),
            columns: [
              {
                binding: 'iwCspCapa', // 기계 조건부 필수 대상값
                header: '[ℓ/min]',
                minWidth: 70,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
              {
                binding: 'iwCspSumCapa', // 수식 (= Qty * IW Consumption[ℓ/min])
                header: 'Sum',
                minWidth: 70,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          {
            /**
             * [25.09.23] 표준사용량 로직 변경
             * - 표준설비코드 기준 라이브러리의 평균값 노출
             * - 라이브러리의 값이 없는 경우 대상에서 제외 (0 포함)
             */
            header: String(t('ut.label.표준사용량', '표준사용량')),
            columns: [
              {
                binding: 'iwStndUseQty',
                header: '[ℓ/min]',
                minWidth: 70,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
        ],
      },
      {
        header: String(t('ut.label.IW for Emergency fire', 'FW')),
        //      collapseTo: 'frwtOtsdDmtrValCtn',
        columns: [
          {
            header: String(t('ut.label.Size', 'Size')),
            columns: [
              {
                binding: 'frwtOtsdDmtrValCtn',
                header: String(t('ut.label.입력값', '입력값')),
                minWidth: 70,
                width: 85,
                isReadOnly: true,
              },
              {
                binding: 'none',
                header: String(t('ut.label.추천값', '추천값')),
                minWidth: 60,
                width: 75,
                isReadOnly: true,
              },
            ],
          },
          {
            header: String(t('ut.label.Connection', 'Connection')),
            columns: [
              {
                binding: 'frwtDevcCnctTpCd',
                header: '[Type]',
                minWidth: 70,
                width: 90,
                isReadOnly: true,
                cssClass: 'WijmoSelect',
                dataMap: new DataMap(code?.devcCnctTpCd || [], 'cmnCd', 'cmnCdNm'),
              },
            ],
          },
          {
            header: String(t('ut.label.Tempurature', 'Tempurature')),
            columns: [
              {
                binding: 'frwtTmprValCtn', // [24.11.11] 숫자 -> 문자 변경 (데이터 검증 제외)
                header: '[℃]',
                minWidth: 70,
                width: 83,
                isReadOnly: true,
              },
            ],
          },
          {
            header: String(t('ut.label.Pressure', 'Pressure')),
            columns: [
              {
                binding: 'frwtPresValCtn', // [24.11.11] 숫자 -> 문자 변경 (데이터 검증 제외)
                header: '[Bar]',
                minWidth: 70,
                width: 80,
                isReadOnly: true,
              },
            ],
          },
          {
            header: String(t("ut.label.Q'ty", "Q'ty")),
            columns: [
              {
                binding: 'frwtPntCnt',
                header: '[Point]',
                minWidth: 70,
                width: 92,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          {
            header: String(t('ut.label.Drain', 'Drain')),
            columns: [
              {
                binding: 'frwtDrnCapa',
                header: '[ℓ/min]',
                minWidth: 70,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
              {
                binding: 'frwtDrnSumCapa',
                header: 'Sum',
                minWidth: 70,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          {
            header: String(t('ut.label.Consumption', 'Consumption')),
            columns: [
              {
                binding: 'frwtCspCapa', // 기계 조건부 필수 대상값
                header: '[ℓ/min]',
                minWidth: 70,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
              {
                binding: 'frwtCspSumCapa', // 수식 (= Qty * IW2 Consumption[ℓ/min])
                header: 'Sum',
                minWidth: 70,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          {
            /**
             * [25.09.23] 표준사용량 로직 변경
             * - 표준설비코드 기준 라이브러리의 평균값 노출
             * - 라이브러리의 값이 없는 경우 대상에서 제외 (0 포함)
             */
            header: String(t('ut.label.표준사용량', '표준사용량')),
            columns: [
              {
                binding: 'frwtStndUseQty',
                header: '[ℓ/min]',
                minWidth: 70,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
        ],
      },
      {
        header: String(t('ut.label.PCW', 'PCW')),
        //   collapseTo: 'coolOtsdSizeValCtn',
        columns: [
          {
            header: String(t('ut.label.Size', 'Size')),
            columns: [
              {
                binding: 'coolOtsdSizeValCtn',
                header: String(t('ut.label.입력값', '입력값')),
                minWidth: 70,
                width: 85,
                isReadOnly: true,
              },
              {
                binding: 'none',
                header: String(t('ut.label.추천값', '추천값')),
                minWidth: 60,
                width: 75,
                isReadOnly: true,
              },
            ],
          },
          {
            header: String(t('ut.label.Connection', 'Connection')),
            columns: [
              {
                binding: 'coolDevcCnctTpCd',
                header: '[Type]',
                minWidth: 70,
                width: 90,
                isReadOnly: true,
                cssClass: 'WijmoSelect',
                dataMap: new DataMap(code?.devcCnctTpCd || [], 'cmnCd', 'cmnCdNm'),
              },
            ],
          },
          {
            header: String(t('ut.label.Tempurature', 'Tempurature')),
            columns: [
              {
                binding: 'coolTmprValCtn', // [24.11.11] 숫자 -> 문자 변경 (데이터 검증 제외)
                header: '[℃]',
                minWidth: 70,
                width: 85,
                isReadOnly: true,
              },
            ],
          },
          {
            header: String(t('ut.label.Pressure', 'Pressure')),
            columns: [
              {
                binding: 'coolPresValCtn', // [24.11.11] 숫자 -> 문자 변경 (데이터 검증 제외)
                header: '[Bar]',
                minWidth: 70,
                width: 80,
                isReadOnly: true,
              },
            ],
          },
          {
            header: String(t("ut.label.Q'ty", "Q'ty")),
            columns: [
              {
                binding: 'coolPntCnt',
                header: '[Point]',
                minWidth: 70,
                width: 92,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          {
            header: String(t('ut.label.Consumption', 'Consumption')),
            columns: [
              {
                binding: 'coolCspCapa', // 기계 조건부 필수 대상값
                header: '[ℓ/min]',
                minWidth: 70,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
              {
                binding: 'coolCspSumCapa', // 수식 (= Qty * PCW Consumption[ℓ/min])
                header: 'Sum',
                minWidth: 70,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          {
            /**
             * [25.09.23] 표준사용량 로직 변경
             * - 표준설비코드 기준 라이브러리의 평균값 노출
             * - 라이브러리의 값이 없는 경우 대상에서 제외 (0 포함)
             */
            header: String(t('ut.label.표준사용량', '표준사용량')),
            columns: [
              {
                binding: 'coolStndUseQty',
                header: '[ℓ/min]',
                minWidth: 70,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
        ],
      },
      {
        header: String(t('ut.label.WW', 'WW')),
        //     collapseTo: 'wwOtsdDmtrValCtn',
        columns: [
          {
            header: String(t('ut.label.Size', 'Size')),
            columns: [
              {
                binding: 'wwOtsdDmtrValCtn',
                header: String(t('ut.label.입력값', '입력값')),
                minWidth: 70,
                width: 85,
                isReadOnly: true,
              },
              {
                binding: 'none',
                header: String(t('ut.label.추천값', '추천값')),
                minWidth: 60,
                width: 75,
                isReadOnly: true,
              },
            ],
          },
          {
            header: String(t('ut.label.Connection', 'Connection')),
            columns: [
              {
                binding: 'wwDevcCnctTpCd',
                header: '[Type]',
                minWidth: 70,
                width: 90,
                isReadOnly: true,
                cssClass: 'WijmoSelect',
                dataMap: new DataMap(code?.devcCnctTpCd || [], 'cmnCd', 'cmnCdNm'),
              },
            ],
          },
          {
            header: String(t('ut.label.Tempurature', 'Tempurature')),
            columns: [
              {
                binding: 'wwTmprValCtn', // [24.11.11] 숫자 -> 문자 변경 (데이터 검증 제외)
                header: '[℃]',
                minWidth: 70,
                width: 85,
                isReadOnly: true,
              },
            ],
          },
          {
            header: String(t('ut.label.Pressure', 'Pressure')),
            columns: [
              {
                binding: 'wwPresValCtn', // [24.11.11] 숫자 -> 문자 변경 (데이터 검증 제외)
                header: '[Bar]',
                minWidth: 70,
                width: 80,
                isReadOnly: true,
              },
            ],
          },
          {
            header: String(t("ut.label.Q'ty", "Q'ty")),
            columns: [
              {
                binding: 'wwPntCnt',
                header: '[Point]',
                minWidth: 70,
                width: 92,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          {
            header: String(t('ut.label.Consumption', 'Consumption')),
            columns: [
              {
                binding: 'wwCspCapa', // 기계 조건부 필수 대상값
                header: '[ℓ/min]',
                minWidth: 70,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
              {
                binding: 'wwCspSumCapa', // 수식 (= Qty * WW Consumption[ℓ/min])
                header: 'Sum',
                minWidth: 70,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          {
            /**
             * [25.09.23] 표준사용량 로직 변경
             * - 표준설비코드 기준 라이브러리의 평균값 노출
             * - 라이브러리의 값이 없는 경우 대상에서 제외 (0 포함)
             */
            header: String(t('ut.label.표준사용량', '표준사용량')),
            columns: [
              {
                binding: 'wwStndUseQty',
                header: '[ℓ/min]',
                minWidth: 70,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
        ],
      },
      {
        header: String(t('ut.label.Steam', 'Steam')),
        //    collapseTo: 'stemOtsdDmtrValCtn',
        columns: [
          {
            header: String(t('ut.label.Size', 'Size')),
            columns: [
              {
                binding: 'stemOtsdDmtrValCtn',
                header: String(t('ut.label.입력값', '입력값')),
                minWidth: 70,
                width: 85,
                isReadOnly: true,
              },
              {
                binding: 'none',
                header: String(t('ut.label.추천값', '추천값')),
                minWidth: 60,
                width: 75,
                isReadOnly: true,
              },
            ],
          },
          {
            header: String(t('ut.label.Connection', 'Connection')),
            columns: [
              {
                binding: 'stemDevcCnctTpCd',
                header: '[Type]',
                minWidth: 70,
                width: 90,
                isReadOnly: true,
                cssClass: 'WijmoSelect',
                dataMap: new DataMap(code?.devcCnctTpCd || [], 'cmnCd', 'cmnCdNm'),
              },
            ],
          },
          {
            header: String(t('ut.label.Condensator connection', 'Condensator connection')),
            columns: [
              {
                binding: 'stemCwtSize',
                header: 'size [Φ]',
                minWidth: 70,
                width: 96,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
              {
                binding: 'stemCwtDevcCnctTpCd',
                header: '[Type]',
                minWidth: 70,
                width: 96,
                isReadOnly: true,
                cssClass: 'WijmoSelect',
                dataMap: new DataMap(code?.devcCnctTpCd || [], 'cmnCd', 'cmnCdNm'),
              },
            ],
          },
          {
            header: String(t('ut.label.Pressure', 'Pressure')),
            columns: [
              {
                binding: 'stemPresValCtn', // [24.11.11] 숫자 -> 문자 변경 (데이터 검증 제외)
                header: '[Bar]',
                minWidth: 70,
                width: 80,
                isReadOnly: true,
              },
            ],
          },
          {
            header: String(t("ut.label.Q'ty", "Q'ty")),
            columns: [
              {
                binding: 'stemPntCnt',
                header: '[Point]',
                minWidth: 70,
                width: 100,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          {
            header: String(t('ut.label.Consumption', 'Consumption')),
            columns: [
              {
                binding: 'stemCspCapa', // 기계 조건부 필수 대상값
                header: '[t/hr]',
                minWidth: 70,
                width: 85,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
              {
                binding: 'stemCspSumCapa', // 수식 (= Qty * Steam Consumption[ℓ/min])
                header: 'Sum',
                minWidth: 70,
                width: 85,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          {
            /**
             * [25.09.23] 표준사용량 로직 변경
             * - 표준설비코드 기준 라이브러리의 평균값 노출
             * - 라이브러리의 값이 없는 경우 대상에서 제외 (0 포함)
             */
            header: String(t('ut.label.표준사용량', '표준사용량')),
            columns: [
              {
                binding: 'stemStndUseQty',
                header: '[t/hr]',
                minWidth: 70,
                width: 85,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
        ],
      },
      {
        header: String(t('ut.label.NG', 'NG')),
        //    collapseTo: 'lngOtsdDmtrValCtn',
        columns: [
          {
            header: String(t('ut.label.Size', 'Size')),
            columns: [
              {
                binding: 'lngOtsdDmtrValCtn',
                header: String(t('ut.label.입력값', '입력값')),
                minWidth: 70,
                width: 85,
                isReadOnly: true,
              },
              {
                binding: 'none',
                header: String(t('ut.label.추천값', '추천값')),
                minWidth: 60,
                width: 75,
                isReadOnly: true,
              },
            ],
          },
          {
            header: String(t('ut.label.Connection', 'Connection')),
            columns: [
              {
                binding: 'lngDevcCnctTpCd',
                header: '[Type]',
                minWidth: 70,
                width: 90,
                isReadOnly: true,
                cssClass: 'WijmoSelect',
                dataMap: new DataMap(code?.devcCnctTpCd || [], 'cmnCd', 'cmnCdNm'),
              },
            ],
          },
          {
            header: String(t('ut.label.Pressure', 'Pressure')),
            columns: [
              {
                binding: 'lngPresValCtn', // [24.11.11] 숫자 -> 문자 변경 (데이터 검증 제외)
                header: '[Bar]',
                minWidth: 70,
                width: 80,
                isReadOnly: true,
              },
            ],
          },
          {
            header: String(t("ut.label.Q'ty", "Q'ty")),
            columns: [
              {
                binding: 'lngPntCnt',
                header: '[Point]',
                minWidth: 70,
                width: 92,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          {
            header: String(t('ut.label.Consumption', 'Consumption')),
            columns: [
              {
                binding: 'lngCspCapa', // 기계 조건부 필수 대상값
                header: '[Nm3/hr]',
                minWidth: 70,
                width: 110,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
              {
                binding: 'lngCspSumCapa', // 수식 (= Qty * NG Consumption[ℓ/min])
                header: 'Sum',
                minWidth: 70,
                width: 90,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          {
            /**
             * [25.09.23] 표준사용량 로직 변경
             * - 표준설비코드 기준 라이브러리의 평균값 노출
             * - 라이브러리의 값이 없는 경우 대상에서 제외 (0 포함)
             */
            header: String(t('ut.label.표준사용량', '표준사용량')),
            columns: [
              {
                binding: 'lngStndUseQty',
                header: '[Nm3/hr]',
                minWidth: 70,
                width: 108,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
        ],
      },
      {
        header: String(t('ut.label.Exhaust air [Return(to Air Handling Unit)]', 'Return Air')),
        //   collapseTo: 'exhaOtsdDmtrValCtn',
        columns: [
          {
            header: String(t('ut.label.Size', 'Size')),
            columns: [
              {
                binding: 'exhaOtsdDmtrValCtn',
                header: String(t('ut.label.입력값', '입력값')),
                minWidth: 70,
                width: 85,
                isReadOnly: true,
              },
              {
                binding: 'none',
                header: String(t('ut.label.추천값', '추천값')),
                minWidth: 60,
                width: 75,
                isReadOnly: true,
              },
            ],
          },
          {
            header: String(t('ut.label.Connection', 'Connection')),
            columns: [
              {
                binding: 'insdExhaCnctTpCd',
                header: '[Type]',
                minWidth: 70,
                width: 85,
                isReadOnly: true,
                cssClass: 'WijmoSelect',
                dataMap: new DataMap(code?.devcCnctTpCd || [], 'cmnCd', 'cmnCdNm'),
              },
            ],
          },
          {
            header: String(t('ut.label.Collector Type', 'Collector Type')),
            columns: [
              {
                binding: 'insdExhaCllrTpCd',
                header: '[Type]',
                minWidth: 70,
                width: 93,
                isReadOnly: true,
                cssClass: 'WijmoSelect',
                dataMap: new DataMap(code?.exhaCllrTpCd || [], 'cmnCd', 'cmnCdNm'),
              },
            ],
          },
          {
            header: String(t('ut.label.Tempurature', 'Tempurature')),
            columns: [
              {
                binding: 'insdExhaTmprValCtn', // [24.11.11] 숫자 -> 문자 변경 (데이터 검증 제외)
                header: '[℃]',
                minWidth: 70,
                width: 80,
                isReadOnly: true,
              },
            ],
          },
          {
            header: String(t("ut.label.Q'ty", "Q'ty")),
            columns: [
              {
                binding: 'insdExhaPntCnt',
                header: '[Point]',
                minWidth: 70,
                width: 92,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          {
            header: String(t('ut.label.Consumption', 'Consumption')),
            columns: [
              {
                binding: 'insdExhaCspCapa', // 기계 조건부 필수 대상값
                header: '[CMH]',
                minWidth: 70,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
              {
                binding: 'insdExhaCspSumCapa', // 수식 (= Qty * Exhaust air Return Consumption[ℓ/min])
                header: 'Sum',
                minWidth: 70,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          {
            /**
             * [25.09.23] 표준사용량 로직 변경
             * - 표준설비코드 기준 라이브러리의 평균값 노출
             * - 라이브러리의 값이 없는 경우 대상에서 제외 (0 포함)
             */
            header: String(t('ut.label.표준사용량', '표준사용량')),
            columns: [
              {
                binding: 'insdExhaStndUseQty',
                header: '[CMH]',
                minWidth: 70,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
        ],
      },
      {
        header: String(t('ut.label.Exhaust air [Ventilation(to Outside)]', 'Exhaust air')),
        //  collapseTo: 'otsdExhaOtsdDmtrValCtn',
        columns: [
          {
            header: String(t('ut.label.Size', 'Size')),
            columns: [
              {
                binding: 'otsdExhaOtsdDmtrValCtn',
                header: String(t('ut.label.입력값', '입력값')),
                minWidth: 70,
                width: 85,
                isReadOnly: true,
              },
              {
                binding: 'none',
                header: String(t('ut.label.추천값', '추천값')),
                minWidth: 60,
                width: 75,
                isReadOnly: true,
              },
            ],
          },
          {
            header: String(t('ut.label.Connection', 'Connection')),
            columns: [
              {
                binding: 'otsdExhaCnctTpCd',
                header: '[Type]',
                minWidth: 70,
                width: 90,
                isReadOnly: true,
                cssClass: 'WijmoSelect',
                dataMap: new DataMap(code?.devcCnctTpCd || [], 'cmnCd', 'cmnCdNm'),
              },
            ],
          },
          {
            header: String(t('ut.label.Collector Type', 'Collector Type')),
            columns: [
              {
                binding: 'otsdExhaCllrTpCd', // [25.09.23] 추가
                header: '[Type]',
                minWidth: 70,
                width: 93,
                isReadOnly: true,
                cssClass: 'WijmoSelect',
                dataMap: new DataMap(code?.exhaCllrTpCd || [], 'cmnCd', 'cmnCdNm'),
              },
            ],
          },
          {
            header: String(t('ut.label.Tempurature', 'Tempurature')),
            columns: [
              {
                binding: 'otsdExhaTmprValCtn', // [25.09.23] 추가
                header: '[℃]',
                minWidth: 70,
                width: 80,
                isReadOnly: true,
              },
            ],
          },
          {
            header: String(t("ut.label.Q'ty", "Q'ty")),
            columns: [
              {
                binding: 'otsdExhaPntCnt',
                header: '[Point]',
                minWidth: 70,
                width: 92,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          {
            header: String(t('ut.label.Consumption', 'Consumption')),
            columns: [
              {
                binding: 'otsdExhaCspCapa', // 기계 조건부 필수 대상값
                header: '[CMH]',
                minWidth: 70,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
              {
                binding: 'otsdExhaCspSumCapa', // 수식 (= Qty * Exhaust air [Ventilation Consumption[ℓ/min])
                header: 'Sum',
                minWidth: 70,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          {
            /**
             * [25.09.23] 표준사용량 로직 변경
             * - 표준설비코드 기준 라이브러리의 평균값 노출
             * - 라이브러리의 값이 없는 경우 대상에서 제외 (0 포함)
             */
            header: String(t('ut.label.표준사용량', '표준사용량')),
            columns: [
              {
                binding: 'otsdExhaStndUseQty',
                header: '[CMH]',
                minWidth: 70,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
        ],
      },
      // 26.08.05 CSR E20260727-000228 Vacuum Pump 추가
      {
        header: String(t('ut.label.Vacuum Pump', 'Vacuum Pump')),
        columns: [
          {
            header: String(t('ut.label.Size', 'Size')),
            columns: [
              {
                binding: 'vacmPumpOtsdDmtrValCtn',
                header: String(t('ut.label.입력값', '입력값')),
                minWidth: 70,
                width: 85,
                isReadOnly: true,
              },
              {
                binding: 'none',
                header: String(t('ut.label.추천값', '추천값')),
                minWidth: 60,
                width: 75,
                isReadOnly: true,
              },
            ],
          },
          {
            header: String(t('ut.label.Connection', 'Connection')),
            columns: [
              {
                binding: 'vacmPumpCnctTpCd',
                header: '[Type]',
                minWidth: 70,
                width: 90,
                isReadOnly: true,
                cssClass: 'WijmoSelect',
                dataMap: new DataMap(code?.devcCnctTpCd || [], 'cmnCd', 'cmnCdNm'),
              },
            ],
          },
          {
            header: String(t('ut.label.Collector Type', 'Collector Type')),
            columns: [
              {
                binding: 'vacmPumpCllrTpCd', // [25.09.23] 추가
                header: '[Type]',
                minWidth: 70,
                width: 93,
                isReadOnly: true,
                cssClass: 'WijmoSelect',
                dataMap: new DataMap(code?.vacmPumpCllrTpCd || [], 'cmnCd', 'cmnCdNm'),
              },
            ],
          },
          {
            header: String(t('ut.label.Tempurature', 'Tempurature')),
            columns: [
              {
                binding: 'vacmPumpTmprValCtn',
                header: '[℃]',
                minWidth: 70,
                width: 80,
                isReadOnly: true,
              },
            ],
          },
          {
            header: String(t('ut.label.Pressure', 'Pressure')),
            columns: [
              {
                binding: 'vacmPumpPresValCtn',
                header: '[kPa]',
                minWidth: 70,
                width: 80,
                isRequired: false,
                isReadOnly: true,
                dataType: DataType.Number,
                align: 'right',
                editor: new input.InputNumber(document.createElement('div'), {
                  max: 0,
                }),
              },
            ],
          },
          {
            header: String(t("ut.label.Q'ty", "Q'ty")),
            columns: [
              {
                binding: 'vacmPumpPntCnt',
                header: '[Point]',
                minWidth: 70,
                width: 92,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          {
            header: String(t('ut.label.Consumption', 'Consumption')),
            columns: [
              {
                binding: 'vacmPumpCspCapa', // 기계 조건부 필수 대상값
                header: '[CMH]',
                minWidth: 70,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
              {
                binding: 'vacmPumpCspSumCapa', // 수식 (= Qty * va)
                header: 'Sum',
                minWidth: 70,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          {
            /**
             * [25.09.23] 표준사용량 로직 변경
             * - 표준설비코드 기준 라이브러리의 평균값 노출
             * - 라이브러리의 값이 없는 경우 대상에서 제외 (0 포함)
             */
            header: String(t('ut.label.표준사용량', '표준사용량')),
            columns: [
              {
                binding: 'vacmPumpStndUseQty',
                header: '[CMH]',
                minWidth: 70,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
        ],
      },
      {
        header: String(t('ut.label.Supply Air', 'Supply air')),
        //     collapseTo: 'suarOtsdDmtrValCtn',
        columns: [
          {
            header: String(t('ut.label.Size', 'Size')),
            columns: [
              {
                binding: 'suarOtsdDmtrValCtn',
                header: String(t('ut.label.입력값', '입력값')),
                minWidth: 70,
                width: 85,
                isReadOnly: true,
              },
              {
                binding: 'none',
                header: String(t('ut.label.추천값', '추천값')),
                minWidth: 60,
                width: 75,
                isReadOnly: true,
              },
            ],
          },
          {
            header: String(t('ut.label.Connection', 'Connection')),
            columns: [
              {
                binding: 'suarDevcCnctTpCd',
                header: '[Type]',
                minWidth: 70,
                width: 90,
                isReadOnly: true,
                cssClass: 'WijmoSelect',
                dataMap: new DataMap(code?.devcCnctTpCd || [], 'cmnCd', 'cmnCdNm'),
              },
            ],
          },
          /*
          [24.12.02] suarDevcCnctTpCd과 중복항목이므로 삭제
          {
            binding: 'suarCllrTpCd',
            header: String(t('ut.label.Connection Type[Duct/Pipe]', 'Connection Type[Duct/Pipe]')),
            width: 100,
            isReadOnly: true,
            cssClass: 'WijmoSelect',
            dataMap: new DataMap(code?.suarCllrTpCd || [], 'cmnCd', 'cmnCdNm'),
          },
          */
          {
            header: String(t('ut.label.Tempurature', 'Tempurature')),
            columns: [
              {
                binding: 'suarTmprValCtn', // [25.09.23] 추가
                header: '[℃]',
                minWidth: 70,
                width: 80,
                isRequired: false,
                isReadOnly: true,
              },
            ],
          },
          {
            header: String(t("ut.label.Q'ty", "Q'ty")),
            columns: [
              {
                binding: 'suarPntCnt',
                header: '[Point]',
                minWidth: 70,
                width: 92,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          {
            header: String(t('ut.label.Consumption', 'Consumption')),
            columns: [
              {
                binding: 'suarCspCapa', // 기계 조건부 필수 대상값
                header: '[CMH]',
                minWidth: 70,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
              {
                binding: 'suarCspSumCapa', // 수식 (= Qty * Supply air Consumption[ℓ/min])
                header: 'Sum',
                minWidth: 70,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
          {
            /**
             * [25.09.23] 표준사용량 로직 변경
             * - 표준설비코드 기준 라이브러리의 평균값 노출
             * - 라이브러리의 값이 없는 경우 대상에서 제외 (0 포함)
             */
            header: String(t('ut.label.표준사용량', '표준사용량')),
            columns: [
              {
                binding: 'suarStndUseQty',
                header: '[CMH]',
                minWidth: 70,
                width: 95,
                isReadOnly: true,
                align: 'right',
                cellTemplate: (params) =>
                  Number(params.item.machEqpQty || 0) < 1
                    ? DefaultNoneNumberCellTemplate(params)
                    : DefaultZeroNumberCellTemplate(params),
              },
            ],
          },
        ],
      },
      {
        header: String(t('ut.label.Equipment Specification', 'Equipment Specification')),
        // collapseTo: 'eqpDimWthLen',
        isCollapsed: false,
        columns: [
          {
            header: String(t('ut.label.Dimension[mm]', 'Dimension[mm]')),
            columns: [
              {
                binding: 'eqpDimWthLen',
                header: String(t('ut.label.Width', 'Width')),
                minWidth: 70,
                width: 190,
                isReadOnly: true,
                align: 'right',
                cellTemplate: DefaultZeroNumberCellTemplate,
              },
              {
                binding: 'eqpDimDpthNvl',
                header: String(t('ut.label.Depth', 'Depth')),
                minWidth: 70,
                width: 100,
                isReadOnly: true,
                align: 'right',
                cellTemplate: DefaultZeroNumberCellTemplate,
              },
              {
                binding: 'eqpDimHigtNvl',
                header: String(t('ut.label.Height', 'Height')),
                minWidth: 70,
                width: 100,
                isReadOnly: true,
                align: 'right',
                cellTemplate: DefaultZeroNumberCellTemplate,
              },
            ],
          },
          {
            header: String(t('ut.label.Weight[kg]', 'Weight[kg]')),
            columns: [
              {
                binding: 'eqpWgt',
                header: String(t('ut.label.전체중량', '전체중량')),
                minWidth: 70,
                width: 110,
                isReadOnly: true,
                align: 'right',
                cellTemplate: DefaultZeroNumberCellTemplate,
              },
            ],
          },
        ],
      },
      {
        binding: 'rmk',
        header: String(t('ut.label.Remark', 'Remark')),
        minWidth: 70,
        width: 100,
        isReadOnly: true,
      },
      // {
      //   binding: 'dataInsUserNm',
      //   header: String(t('com.label.작성자', '작성자')),
      //   minWidth: 70,
      //   width: 100,
      //   align: 'center',
      //   isReadOnly: true,
      // },
      // {
      //   binding: 'dataInsDtm',
      //   header: String(t('com.label.작성일자', '작성일자')),
      //   minWidth: 70,
      //   width: 120,
      //   align: 'center',
      //   isReadOnly: true,
      // },
      {
        binding: 'wrtUserId',
        header: String(t('com.label.수정자', '수정자')),
        minWidth: 70,
        width: 100,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'wrtDtm',
        header: String(t('com.label.수정일자', '수정일자')),
        minWidth: 70,
        width: 120,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'rvwDataUpdUserNm',
        header: String(t('com.label.검토자', '검토자')),
        minWidth: 70,
        width: 100,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'rvwDataUpdDtm',
        header: String(t('com.label.검토일자', '검토일자')),
        minWidth: 70,
        width: 120,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'utmId',
        visible: false,
      },
      {
        binding: 'planProcId',
        visible: false,
      },
      /* {
        binding: 'utmSeq',
        visible: false,
      },*/
      {
        binding: 'eqclId',
        visible: false,
      },
      {
        binding: 'eqpMainId',
        visible: false,
      },
      {
        binding: 'eqpMchId',
        visible: false,
      },
      {
        binding: 'exlUpldUseYn',
        visible: false,
      },
    ];
  }, [code, isChangeColumn]);

  const searchRegistMatrix = (params) => {
    findUtMatrix('review', params).then((result) => {
      setRowData(result || []);
    });
  };

  const handleSave = (showMsg = true) => {
    return new Promise((resolve) => {
      if (!(rowData || []).filter((o) => o.crudKey).length) {
        /*
        // [24.08.19] 탭 이동 시 변경사항 없는 경우 자동저장 안내문구 미노출
        if (showMsg) {
          openMessageBar({
            type: 'warning',
            content: t('ut.label.변경사항이 없습니다.', '변경사항이 없습니다.'),
          });
        }
        */
        resolve(true);
        return;
      }

      saveUtMatrix('review', rowData)
        .then((res) => {
          if (showMsg) {
            openMessageBar({
              type: res?.successOrNot === 'Y' ? 'confirm' : 'error',
              content:
                res?.successOrNot === 'Y'
                  ? t('com.msg.저장되었습니다.', '저장되었습니다.')
                  : t('com.msg.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
            });
          }
          if (res?.successOrNot === 'Y') {
            searchRegistMatrix(condition);
          }
        })
        .finally(() => resolve(true));
    });
  };

  const onInitialized = (grid) => {
    setFlexRef(grid);
    gridRef.current = grid;
    grid.itemFormatter = onItemFormatter;
    /*
    grid.formatItem.addHandler(function (s, e) {
      if (s.cells === e.panel) {
        const column = s.columns[e.col];
        const format = column.format;
        const dataType = column.dataType;
        if (dataType === DataType.Number && format) {
          // [25.10.01] 자연수(ex. 수량)를 제외한 숫자는 소수점 1자리까지 표현(2자리에서 반올림 처리) (ex. 230.456 입력 시 실제 데이터는 230.5 저장)
          const binding = column.binding;
          s.rows[e.row].dataItem[binding] = e.cell.innerText ? convertNumber(e.cell.innerText) : '';
        }
      }
    });
    */
    grid.loadedRows.addHandler(() => {
      const items = grid.collectionView ? grid.collectionView.sourceCollection : grid.itemsSource;
      if (!items || !Array.isArray(items)) {
        return;
      }

      items.forEach((item, rowIndex) => {
        item.dmdElpwCapa = calculateDmdElpwCapa(
          item.elpwEqpQty,
          item.elpwCapa,
          item.dmdElpwTarfNvl
        );
        grid.columns.forEach((col, colIndex) => {
          const value = grid.getCellData(rowIndex, colIndex, false);
          const content = CommonUtil.safeHtmlContents(value, false);

          grid.setCellData(rowIndex, colIndex, content, true);
        });
      });

      grid.invalidate();
    });

    grid.hostElement.addEventListener('click', (e) => {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);

      if (ht.panel === grid.cells) {
        const item = grid.rows[ht.row].dataItem;
        const binding = grid.columns[ht.col].binding;

        if (e.target instanceof HTMLButtonElement) {
          const id = e.target.id;
          // 표준설비코드 클릭
          if ('stndEqpId' === id) {
            setUtMatrixRegistHistoryCondition({
              stndEqpId: item.stndEqpId,
            });
            setUtMatrixRegistHistoryModalOpen(true);
          }
        }

        if ('machEqpNm' === binding) {
          if (item.machEqpNm && item.tagIssuYn === 'Y') {
            if (item.utmId && item.planProcId && item.utmSeq) {
              setHookupTagCondition({
                utmId: item.utmId,
                planProcId: item.planProcId,
                utmSeq: item.utmSeq,
                tagDivsCd: 'MECH',
              });
              setOpenHookupTagPopup(true);
            }
          }
        }
        if ('elpwEqpNm' === binding) {
          if (item.elpwEqpNm && item.tagIssuYn === 'Y') {
            if (item.utmId && item.planProcId && item.utmSeq) {
              setHookupTagCondition({
                utmId: item.utmId,
                planProcId: item.planProcId,
                utmSeq: item.utmSeq,
                tagDivsCd: 'ELEC',
              });
              setOpenHookupTagPopup(true);
            }
          }
        }
      }
    });
  };

  const downloadExcel = async (templateType) => {
    downloadExcelTemplates(templateType, '1');
  };

  const onBeginningEdit = useEvent((grid, e) => {
    const binding = grid.columns[e.col].binding;
    const item = grid.rows[e.row].dataItem;
    /*
    // [25.09.23] 표준사용량 로직 변경 (표준설비코드 기준 라이브러리의 평균값 노출)
    // 표준사용량
    if (
      [
        'elpwStndUseQty',
        'cdaStndUseQty',
        'nitrStndUseQty',
        'iwStndUseQty',
        'frwtStndUseQty',
        'coolStndUseQty',
        'wwStndUseQty',
        'stemStndUseQty',
        'lngStndUseQty',
        'insdExhaStndUseQty',
        'otsdExhaStndUseQty',
        'suarStndUseQty',
      ].includes(binding)
    ) {
      // 작성/검토 상태가 아닌 경우 readonly
      if (!(isUtWriter || isUtComplete || isUtReview)) {
        e.cancel = true;
      }
      // Electricity > 표준사용량 - 전력설비수량(elpwEqpQty)이 0인 경우 readonly
      if ('elpwStndUseQty' === binding && Number(item.elpwEqpQty || 0) < 1) {
        e.cancel = true;
      }
    }
    */
    // TODO 검토화면에서 UT Manager 권한인 경우 입력
    // 수용율
    if (['dmdElpwTarfNvl', 'cdsCapa'].includes(binding)) {
      // Electricity > 수용율 - 전력설비수량(elpwEqpQty)이 0인 경우 readonly
      if (Number(item.elpwEqpQty || 0) < 1) {
        e.cancel = true;
      }
    }
  });

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row].dataItem;
      /*
      // [25.09.23] 표준사용량 로직 변경 (표준설비코드 기준 라이브러리의 평균값 노출)
      // 표준사용량
      if (
        [
          'elpwStndUseQty',
          'cdaStndUseQty',
          'nitrStndUseQty',
          'iwStndUseQty',
          'frwtStndUseQty',
          'coolStndUseQty',
          'wwStndUseQty',
          'stemStndUseQty',
          'lngStndUseQty',
          'insdExhaStndUseQty',
          'otsdExhaStndUseQty',
          'suarStndUseQty',
        ].includes(binding)
      ) {
        // 작성/검토 상태가 아닌 경우 readonly
        if (!(isUtWriter || isUtComplete || isUtReview)) {
          cell.ariaReadOnly = true;
        }
        // Electricity > 표준사용량 - 전력설비수량(elpwEqpQty)이 0인 경우 readonly
        if ('elpwStndUseQty' === binding && Number(item.elpwEqpQty || 0) < 1) {
          cell.ariaReadOnly = true;
        }
      }
      */
      // TODO 검토화면에서 UT Manager 권한인 경우 입력
      // 수용율
      if (['dmdElpwTarfNvl', 'cdsCapa'].includes(binding)) {
        // Electricity > 수용율 - 전력설비수량(elpwEqpQty)이 0인 경우 readonly
        if (Number(item.elpwEqpQty || 0) < 1) {
          cell.ariaReadOnly = true;
        }
      }
    }
  });

  //검토진행
  const handleSubmitReview = () => {
    const fixedData = rowData.filter((o) => o.crudKey === CrudCode.UPDATE);
    if (fixedData.length > 0) {
      openCommonModal({
        modalType: 'confirm',
        content: t(
          'com.label.변경된 수용율이 있습니다. 저장 후 진행하시겠습니까?',
          '변경된 수용율이 있습니다. 저장 후 진행하시겠습니까?'
        ),
        yesCallback: () => {
          handleSave();
          props?.onSubmit?.();
        },
      });
    } else {
      props?.onSubmit?.();
    }
  };

  //검토완료
  const handleSubmitReviewComplete = () => {
    if (!condition.utmId) return;

    saveUtMatrix('review', rowData).then((res) => {
      if (res?.successOrNot === 'Y') {
        props?.onSubmitComplete?.();
      }
    });
  };

  //검증
  const handleModify = () => {
    const fixedData = rowData.filter((o) => o.crudKey === CrudCode.UPDATE);
    if (fixedData.length > 0) {
      openCommonModal({
        modalType: 'confirm',
        content: t(
          'com.label.변경된 수용율이 있습니다. 저장 후 진행하시겠습니까?',
          '변경된 수용율이 있습니다. 저장 후 진행하시겠습니까?'
        ),
        yesCallback: async () => {
          await handleSave();
          props?.onModify?.();
        },
      });
    } else {
      props?.onModify?.();
    }
  };
  //보류 링크
  const handleRequestLinkHold = () => {
    const fixedData = rowData.filter((o) => o.crudKey === CrudCode.UPDATE);
    if (fixedData.length > 0) {
      openCommonModal({
        modalType: 'confirm',
        content: t(
          'com.label.변경된 수용율이 있습니다. 저장 후 진행하시겠습니까?',
          '변경된 수용율이 있습니다. 저장 후 진행하시겠습니까?'
        ),
        yesCallback: async () => {
          await handleSave();
          props?.onLinkHold?.();
        },
      });
    } else {
      props?.onLinkHold?.();
    }
  };
  //재작성 링크
  const handleRequestLinkRewrite = () => {
    const fixedData = rowData.filter((o) => o.crudKey === CrudCode.UPDATE);
    if (fixedData.length > 0) {
      openCommonModal({
        modalType: 'confirm',
        content: t(
          'com.label.변경된 수용율이 있습니다. 저장 후 진행하시겠습니까?',
          '변경된 수용율이 있습니다. 저장 후 진행하시겠습니까?'
        ),
        yesCallback: async () => {
          await handleSave();
          props?.onLinkRewrite?.();
        },
      });
    } else {
      props?.onLinkRewrite?.();
    }
  };

  const handleApproval = () => {
    props?.onAppr?.();
  };

  const onCellEditEnding = useEvent((grid, e) => {
    const newVal = grid.activeEditor?.value;
    const data = grid.rows[e.row].dataItem;
    const binding = grid.columns[e.col].binding;
    // Demand Factor
    if ('dmdElpwTarfNvl' === binding) {
      if (!_.isNull(newVal) && newVal != '') {
        if (Number(newVal) > 100 || Number(newVal) < 0) {
          openMessageBar({
            type: 'error',
            content: t('ut.label.0~100 사이로 입력하세요.', '0~100 사이로 입력하세요.'),
          });
          e.cancel = true;
          return;
        }
      }
      data.dmdElpwCapa = calculateDmdElpwCapa(data.elpwEqpQty, data.elpwCapa, newVal);
    }
  });

  const excludePin = useMemo(() => {
    const convertFlat = (acc, cur) => {
      if ('columns' in cur && Array.isArray(cur?.columns)) {
        return cur.columns.reduce(convertFlat, acc);
      }
      return ![
        'prdnProcCdNm',
        'bizAreaNm',
        'eqclNm',
        'stndEqpId',
        'dtalProcCd',
        'bldgFloCd',
        'istlLocNm',
        'prdnLnNm',
        'prdnPrlLnNm',
        'eqpMainNm',
        'eqpMchNm',
        'eqpUntNm',
        'machEqpNm',
        'elpwEqpNm',
        'machEqpQty',
        'elpwEqpQty',
      ].includes(cur?.binding)
        ? [...acc, cur?.binding]
        : acc;
    };

    return (layoutDefinition || []).reduce(convertFlat, []);
  }, [layoutDefinition]);

  const includePin = useMemo(() => {
    const convertFlat = (acc, cur) => {
      if ('columns' in cur && Array.isArray(cur?.columns)) {
        return cur.columns.reduce(convertFlat, acc);
      }
      return [...acc, cur?.binding];
    };

    return (layoutDefinition || []).reduce(convertFlat, []);
  }, [layoutDefinition]);

  // [26.01.26] CSR E20260106-000678 검토화면 천단위 숫자 인식되게 변경(컴마삭제)
  const handleDownloadExcel = () => {
    const book = wjGridXlsx.FlexGridXlsxConverter.saveAsync(flexRef, {
      includeColumnHeaders: true,
      includeRowHeaders: true,
      formatItem: (args) => {
        const xlsxCell = args.xlsxCell;
        const cell = args.getFormattedCell();
        const text = cell?.textContent?.trim();
        const delCommaValue = text?.replace(/,/g, '');
        xlsxCell.value = delCommaValue;
        if (!text) {
          // [25.12.12] textContent 없는 경우 dataItem값 export
          const grid = args?.panel?.grid;
          if (!grid) return;
          if (args.panel === grid?.cells) {
            const col = grid?.columns?.[args.col];
            const item = grid?.rows?.[args.row]?.dataItem;
            if (item && col?.binding) {
              xlsxCell.value = item[col.binding] || '';
            }
          }
        }
      },
    });
    book.sheets[0].name = t('ut.label.UT Matrix 작성 대상', 'UT Matrix 작성 대상');
    book.saveAsync(getExcelFileName(t('ut.label.UT Matrix 작성 대상', 'UT Matrix 작성 대상')));
  };

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('ut.label.상세보기', '상세보기')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
          {condition?.utmNo && <div className="info warning">{condition?.utmNo}</div>}
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => searchRegistMatrix(condition)}
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>

          <GridSettingButtons
            gridId={'utMatrixReviewGrid'} // <CustomGrid>에 선언한 id와 동일한 값
            layoutDefinition={layoutDefinition} // 그리드 레이아웃
            changeShownColumns={(result) => {
              // 노출필드 변경이벤트
              // ref를 이용해 <CustomGrid> 내 함수를 실행
              //openLoading(true);
              gridRef2.current?.changeColumn?.(result);
              setIsChangeColumn(true);
              gridRef.current.invalidate();
              //openLoading(false);
            }}
          />
        </ControlBtnGroup>
      </SubTitleLayout>

      <CustomGrid
        ref={gridRef2}
        id={'utMatrixReviewGrid'}
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={props?.isOpenCondition ? defaultGridHeight + 55 : defaultGridHeight + 316 - 55 + 65}
        isSelector={false}
        frozenColumns={12} // [25.11.03] 고도화 핀고정 해제 -> 26.03.24 원복
        isReadOnly={!isUtWriter && !isUtComplete && !isUtReview}
        excludeFilter={['utmSeq']}
        // excludePin={excludePin}
        includePin={includePin} // 26.03.24 pin 추가
        allowPinning={true}
        initialized={onInitialized}
        beginningEdit={onBeginningEdit}
        cellEditEnding={onCellEditEnding}
      />

      <GlobalBtnGroup>
        {/* 작성중 : <검증> <임시저장> <새로고침> UTM02  */}
        {/* 작성완료 : <검토진행> <재작성요청> <보류> <목록> <다운로드>  UTM05 */}
        {/* 검토중 :  <목록> <다운로드>  UTM04 */}
        {/* 검토완료 : <결재요청> <재작성요청> <보류> UTM07 */}
        {/* 그외 (결재중) : <목록> <다운로드>  */}

        {isUtWriter && (
          <>
            <Button css={IconButton.button} className="draft" onClick={handleModify}>
              {t('com.button.검증', '검증')}
            </Button>
            <Button css={IconButton.button} className="draft" onClick={() => handleSave()}>
              {t('com.button.임시저장', '임시저장')}
            </Button>
          </>
        )}
        {isUtComplete && (
          <>
            <Button css={IconButton.button} className="draft" onClick={handleRequestLinkRewrite}>
              {t('com.button.재작성요청', '재작성요청')}
            </Button>
            <Button css={IconButton.button} className="draft" onClick={handleRequestLinkHold}>
              {t('com.button.보류', '보류')}
            </Button>
            <Button css={IconButton.button} className="draft" onClick={handleSubmitReview}>
              {t('com.button.검토진행', '검토진행')}
            </Button>
            <Button css={IconButton.button} className="draft" onClick={handleModify}>
              {t('com.button.검증', '검증')}
            </Button>
            <Button css={IconButton.button} className="draft" onClick={() => handleSave()}>
              {t('com.button.임시저장', '임시저장')}
            </Button>
          </>
        )}
        {isUtReview && (
          // 검토중일때 <검토완료> 버튼 추가
          // <검토완료><재작성요청><보류>
          <>
            <Button css={IconButton.button} className="draft" onClick={handleRequestLinkRewrite}>
              {t('com.button.재작성요청', '재작성요청')}
            </Button>
            <Button css={IconButton.button} className="draft" onClick={handleRequestLinkHold}>
              {t('com.button.보류', '보류')}
            </Button>
            <Button css={IconButton.button} className="draft" onClick={handleModify}>
              {t('com.button.검증', '검증')}
            </Button>
            <Button css={IconButton.button} className="draft" onClick={handleSubmitReviewComplete}>
              {t('com.button.검토완료', '검토완료')}
            </Button>
            <Button css={IconButton.button} className="draft" onClick={() => handleSave()}>
              {t('com.button.임시저장', '임시저장')}
            </Button>
          </>
        )}

        {/*2025.09.16 sungim.kim2 검토완료 상태 일때 버튼 정의*/}
        {isUtReviewComplete && (
          // <재작성요청><보류><결재요청>
          <>
            <Button css={IconButton.button} className="draft" onClick={handleRequestLinkRewrite}>
              {t('com.button.재작성요청', '재작성요청')}
            </Button>
            <Button css={IconButton.button} className="draft" onClick={handleRequestLinkHold}>
              {t('com.button.보류', '보류')}
            </Button>
            <Button css={IconButton.button} className="save" onClick={handleApproval}>
              {isAprText}
            </Button>
          </>
        )}
      </GlobalBtnGroup>
      {isOpenHookupTagPopup && (
        <UtMatrixHookupTagPopup
          open={isOpenHookupTagPopup}
          condition={hookupTagCondition}
          close={() => setOpenHookupTagPopup(false)}
        />
      )}
      {isUtMatrixRegistHistoryModalOpen && (
        <UtMatrixRegistHistoryPoupup
          open={isUtMatrixRegistHistoryModalOpen}
          close={() => setUtMatrixRegistHistoryModalOpen(false)}
          condition={{
            stndEqpId: utMatrixRegistHistoryCondition?.stndEqpId,
          }}
        />
      )}
    </>
  );
};

export default React.forwardRef(DetailSummaryContent);
